import {
  extractDiscountClaims,
  extractMoney,
  readDiscountClaims,
  readServiceFreebieSpans,
  type DiscountReadOptions,
} from "./figures";

/**
 * Per-conversation concession accounting (B7 cross-step guardrail, 8/28).
 *
 * The vault states the failure directly: "5% goodwill here, 'I'll include the
 * chain' there, each message passes every gate, the CONVERSATION cumulatively
 * exceeds the discount cap." Per-message gates cannot see this by
 * construction. It is the jewelry version of task-scoped rollback (Cordon,
 * arXiv:2606.17573), where violations that span steps evade per-call checks.
 *
 * The numeric half of it is already narrow. Section 4's zero-tolerance rule
 * means every figure in a reply, percentages included, has to exact-match a
 * cited KB fact, so an invented "15% off" and a below-list price are both
 * blocked before they get here. What survives is the half that carries no
 * figure at all: "I'll include the chain", "no charge", "on us". Nothing in
 * the gate chain reads those, and they are exactly what the vault's example
 * is made of.
 */

/**
 * Total percentage a conversation may give away: 0, so no drafted reply states a
 * discount. RATIFIED by Kabir 2026-10-03 (decision 3 of the 10-02b list, typed
 * "the first messge shuld not be a discount ,were not running a thrift shop").
 * T3 (section 3). 0 is the interim cap for agent-drafted (automated) messages
 * until Kabir ratifies a limit; the long-run ceiling is still his (see below).
 */
export const MAX_CONVERSATION_DISCOUNT_PCT = 0;

/**
 * How many things one conversation may include at no charge: 0, so no drafted
 * reply gives anything away. The shop's own standing terms, quoted from a cited
 * KB fact ("anything bought here gets free tightening"), are policy and are not
 * counted (`extractConcessions` grounds against the cited facts). RATIFIED by
 * Kabir 2026-10-16 (typed "got it fix all work for hours now", in reply to the
 * rec "One uncited "free X" can slip through. Replies allow one per
 * conversation (for example "free cleaning"). That breaks your no-offer rule.
 * The fix is to block it."; FINDINGS TA-1349). T3 (section 3). Counted rather
 * than valued because a freebie in a DM has no stated price: "I'll include the
 * chain" names no number, and inferring one from the catalog the shop does not
 * have would be a guess with a cap's authority.
 */
export const MAX_CONVERSATION_FREEBIES = 0;

export type Concession =
  | { readonly kind: "discount"; readonly percent: number | null; readonly text: string }
  | { readonly kind: "freebie"; readonly text: string };

export interface ConcessionTally {
  readonly discountPercent: number;
  readonly freebies: number;
  readonly unsizedDiscounts: number;
}

export interface ConcessionBreach {
  readonly cap: "discount" | "freebies";
  readonly detail: string;
}

/**
 * The ways a shop actually gives something away in a DM.
 *
 * Deliberately phrase-shaped rather than keyword-shaped. Matching the bare
 * word "free" would read "feel free to come by" as a concession, and a gate
 * that fires on a greeting is a gate everyone learns to ignore, which is the
 * failure mode section 0 names.
 */
/**
 * The words that make "free" mean available rather than gratis.
 *
 * A stopword list, and named as one rather than buried in the pattern. "Feel
 * free to come by" and "are you free saturday" are ordinary messages, and a
 * gate that fires on them is one the owner learns to dismiss, which is worse
 * than no gate at all (section 0). Not exhaustive, and it does not need to be:
 * a miss here escalates a draft to a human, which is the graceful direction.
 */
const NOT_GRATIS = [
  "to", "of charge", "today", "tomorrow", "tonight", "now", "later", "anytime",
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
  "this", "next", "any", "on",
];

/**
 * TA-1349: "free" that means open, or without a flaw, read as phrases rather than
 * words. At the freebie cap of 0 a misread holds a good reply, and these are the
 * shapes the corpus found: a time ("the 2nd is free at 2pm", "free from 10 until
 * noon"), a booking ("the 3rd is free for a fitting", "free for me", "free if
 * that works"), and a stone or metal without a flaw ("free of inclusions", "free
 * from nickel"). Narrow on purpose: "free at checkout", "free for a year", "free
 * from the 1st of November" and "free of charge" are still read. A DISC-era
 * reader, so a shop draft only: a customer's line is read as base e5faa94 read
 * it (`FREE_SOMETHING_BASE`, customerReadBase.test.ts).
 */
const FREE_NOT_GRATIS_PHRASES = [
  "at \\d",
  "at noon\\b",
  "from \\d",
  "for (?:a |an |your )?(?:fitting|consult|consultation|viewing|appointment|visit|call|chat|pickup|pick-up)s?\\b",
  "for me\\b",
  "if that (?:works|suits)\\b",
  "(?:of|from) (?:any |all )?(?:inclusions?|nickel|flaws?|blemish(?:es)?|scratch(?:es)?|lead|cadmium|chips?|cracks?|tarnish)\\b",
];

const FREE_SOMETHING_BASE = new RegExp(
  `(?<!feel )\\bfree (?!(?:${NOT_GRATIS.join("|")})\\b)[\\w-]+`,
  "gi",
);

const FREE_SOMETHING_SHOP = new RegExp(
  `(?<!feel )\\bfree (?!(?:${NOT_GRATIS.join("|")})\\b)(?!(?:${FREE_NOT_GRATIS_PHRASES.join("|")}))[\\w-]+`,
  "gi",
);

/**
 * TA-571 (decision 93): a passive waiver with no auxiliary ("rush fee waived for
 * you!", "sizing fee waived."), read as one to three words then "waived". The word
 * before "waived" must not be a pronoun, an auxiliary, a filler or an adverb, so
 * "I have waived the fee" and "getting it waived" stay with the verb reading above.
 * AWAITING RATIFICATION: the stop list is the builder's, not Kabir's.
 */
const PASSIVE_WAIVER_STOP = [
  "i", "we", "you", "he", "she", "they", "it", "that", "this", "them", "these", "those", "one", "ones",
  "have", "has", "had", "ve", "ll", "d", "is", "are", "was", "were", "be", "been", "being", "to",
  "will", "would", "can", "could", "shall", "should", "may", "might", "must", "just", "also", "already",
  "and", "or", "but", "then", "if", "so", "get", "gets", "got", "getting",
].join("|");
/**
 * TA-571 fix round 1: the words before "waived" never run back across a conjunction, so
 * "free shipping and sizing waived" is two freebies, not one span that swallows the first;
 * and "waived" with an object after it ("Mike waived the fee") is the verb, read above.
 */
const PASSIVE_WAIVER_JOIN = ["and", "or", "but", "plus", "with", "then", "so", "because"].join("|");
const PASSIVE_WAIVER =
  `(?<![\\w-])(?:(?!(?:${PASSIVE_WAIVER_JOIN})\\b)[\\w-]+\\s+){0,2}(?!(?:${PASSIVE_WAIVER_STOP})\\s+waived\\b)(?![\\w-]+ly\\s+waived\\b)[\\w-]+\\s+waived\\b(?!\\s+(?:the|your|our|any|all|a|an|his|her|their|my|its)\\b)`;

/** The freebie readers for one author: only the bare "free X" reading differs (TA-1349, shop drafts only). */
const buildFreebiePatterns = (author: DiscountReadOptions["author"]): readonly RegExp[] => [
  /\b(?:i(?:'|)ll |we(?:'|)ll |can )?includ(?:e|ing) the [\w-]+/gi,
  /\b(?:i(?:'|)ll |we(?:'|)ll |can )?throw(?:ing)? in (?:the |a )?[\w-]+/gi,
  /\bon us\b/gi,
  /\bno charge\b/gi,
  /\bfree of charge\b/gi,
  author === "shop" ? FREE_SOMETHING_SHOP : FREE_SOMETHING_BASE,
  new RegExp(
    /\bwaiv(?:e|es|ed|ing)\s+(?:(?:the|your|any|our|all)\s+)?[\w-]+|(?<![\w-])[\w-]+(?:\s+[\w-]+)?\s+(?:(?:will|would|can|could|cannot|can['’]t|won['’]t|wont|never|not|has|have|had|ever)\s+){0,3}(?:is|are|was|were|be|been|get|gets|got)\s+waived\b/.source + "|" + PASSIVE_WAIVER,
    "gi",
  ),
  /\bcomplimentary\b/gi,
];
const FREEBIE_PATTERNS_SHOP = buildFreebiePatterns("shop");
const FREEBIE_PATTERNS_CUSTOMER = buildFreebiePatterns("customer");

/**
 * What this message gives away that the shop's standing terms do not already.
 *
 * `groundedText` is the cited KB facts. A freebie that appears in them is
 * policy, not a concession: "anything bought here gets free cleaning for life"
 * is the shop's own terms, and counting it would fill the ledger with things
 * the shop always does until a real concession had no room left. Same shape as
 * the freshness gate, where a figure also grounded in a fresh fact is not
 * stale.
 *
 * DISC fix round 5: `opts.author` names who wrote `reply`, never defaulted
 * (`DiscountReadOptions`, figures.ts). A shop draft is read by every reader;
 * a customer's line (the memory screen) by the readers base e5faa94 had, so
 * a budget is never read as a deal the shop offered.
 */
/** A freebie counted by `extractConcessions`: where its span stands, and where it sits in the output. */
interface CountedSpan {
  readonly at: number;
  readonly end: number;
  readonly index: number;
  named: boolean;
}

/**
 * TA-1349 (TA-1343 Kabir decides 1): the counted spans, kept sorted by start.
 * They never overlap (a span is counted only when none overlaps it), so they
 * are sorted by end too, and the one span that can overlap [at, end) is the
 * first whose end is past `at`: a binary search, where a scan of every span
 * counted so far made a long reply quadratic in its spans.
 */
class CountedSpans {
  private readonly spans: CountedSpan[] = [];

  /** The counted span overlapping [at, end), if any. */
  overlapping(at: number, end: number): CountedSpan | undefined {
    let lo = 0;
    let hi = this.spans.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (this.spans[mid]!.end > at) hi = mid;
      else lo = mid + 1;
    }
    const first = this.spans[lo];
    return first !== undefined && first.at < end ? first : undefined;
  }

  /** Counts a span no counted span overlaps. */
  add(span: CountedSpan): void {
    let lo = 0;
    let hi = this.spans.length;
    while (lo < hi) {
      const mid = (lo + hi) >>> 1;
      if (this.spans[mid]!.at < span.at) lo = mid + 1;
      else hi = mid;
    }
    this.spans.splice(lo, 0, span);
  }
}

export function extractConcessions(
  reply: string,
  groundedText: readonly string[],
  opts: DiscountReadOptions,
): Concession[] {
  const grounded = groundedText.join(" ").toLowerCase();
  const out: Concession[] = [];
  const groundedWaivers = waiverKey(grounded), inGrounded = memoStatesFreebie(grounded), inGroundedWaivers = memoStatesFreebie(groundedWaivers);

  // Read by figures.ts (F-16), fractions included (item 125). Money off ("$50
  // off") has a null percent: counted unsized, never skipped (decision 3, 10-02b).
  for (const claim of readDiscountClaims(reply, opts)) {
    out.push({ kind: "discount", percent: claim.percent, text: claim.text.toLowerCase() });
  }

  /** Where each freebie counted below stands: one span is one freebie, never one wording (round 5; TA-566 offer-8). */
  const counted = new CountedSpans();
  for (const pattern of opts.author === "shop" ? FREEBIE_PATTERNS_SHOP : FREEBIE_PATTERNS_CUSTOMER) {
    for (const m of reply.matchAll(pattern)) {
      const text = m[0].trim().toLowerCase().replace(/^(?:i'?ll |we'?ll |can )/, "");
      const at = m.index ?? 0, end = at + m[0].length;
      // One freebie per span, however many patterns read it; a refused waiver is none.
      if (counted.overlapping(at, end) !== undefined || (/\bwaiv/i.test(text) && isRefusedWaiver(reply, end))) continue;
      // Policy, not a giveaway. A waiver is compared without its filler and auxiliary (TA-571).
      if (inGrounded(text) || (/\bwaived$/.test(text) && isGroundedWaiver(waiverKey(text), inGroundedWaivers))) continue;
      counted.add({ at, end, index: out.length, named: false });
      out.push({ kind: "freebie", text });
    }
  }
  // DISC fix round 2 (review): a service free by its ordinal ("the second
  // sizing is free") is a freebie, read by figures.ts (F-16), never a share off.
  // A DISC reader, so a shop draft only (round 5). DISC fix round 5 (verify4):
  // "the second sizing is free of charge" is one freebie, so a service span
  // that shares its "free" with a freebie counted above is that freebie, named
  // by the service, never a second one.
  if (opts.author === "shop") {
    for (const f of readServiceFreebieSpans(reply)) {
      const text = f.text.toLowerCase();
      if (grounded.includes(text)) continue;
      const same = counted.overlapping(f.at, f.end);
      if (same === undefined) {
        counted.add({ at: f.at, end: f.end, index: out.length, named: true });
        out.push({ kind: "freebie", text });
      } else if (!same.named) {
        same.named = true;
        out[same.index] = { kind: "freebie", text };
      }
    }
  }

  return out;
}

/*
 * TA-384. The discount-question gate's reader (Q-C10), built on Kabir's typed
 * ruling 3 on OPEN-FOR-KABIR row 81 (2026-09-23) and run on the current
 * retrieval query per his typed 2026-09-24 "go with c". T3 territory (price
 * and discount caps, CLAUDE.md section 3): that ruling is the human
 * authorisation for exactly this reader and the gate that uses it, nothing
 * more. AWAITING RATIFICATION (TA-384): every term list below is a first cut,
 * not calibrated on real traffic.
 *
 * One reader, used two ways, so there is no second discount detector on the
 * question side: `isConcessionRequest` gates the reply (retrieval.ts,
 * `admitForQuestion`), and `statesStandingOffer` reads a KB fact with the
 * declarative half of the same vocabulary. The scenario router is NOT a user
 * of it (TA-384 repair): routing decides who handles a line, including the
 * class D unknown row that goes to a human, and that is outside this gate's
 * ruling. F1's rules in verifier.ts read DRAFTS against memory and do not fit
 * a customer line: their DISCOUNT_SENSE words ("rate", "less", "below") would
 * call "is it less than a week" a discount ask. No figure is parsed to a value
 * here, and a "$" amount is found only through figures.ts (F-16): these
 * patterns only say whether a figure sits next to "off" or follows what
 * someone else paid.
 */

/**
 * Number words, so "thirty percent off" reads like "30% off" (TA-213 recorded
 * spelled numbers passing F1). "one" is kept out of the bare form below so "a
 * one off design" is not a discount.
 */
const NUMBER_WORD =
  "(?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|thirteen|fourteen|" +
  "fifteen|sixteen|seventeen|eighteen|nineteen|twenty|thirty|forty|fourty|fifty|sixty|seventy|" +
  "eighty|ninety|hundred|thousand|half|a\\s+few|a\\s+couple(?:\\s+of)?)";
const SPELLED = `${NUMBER_WORD}(?:[\\s-]+(?:and[\\s-]+)?${NUMBER_WORD})*`;
/** Space or hyphen, so "20-percent-off" reads like "20 percent off" (TA-384 repair). */
const GAP = "[\\s-]*";
/** What a figure is counted in. "perc" is a common short form (TA-384 repair). */
const UNIT = `${GAP}(?:%|percent|per${GAP}cent|perc|pct|dollars?|bucks?)`;
const FIGURE_WITH_UNIT = `(?:\\b\\d[\\d,.]*${UNIT}|\\b${SPELLED}${UNIT})`;
/**
 * "off" as a price word: not the first half of "off-white" or "off-the-shelf"
 * (TA-384 repair, "two off-white pearl earrings" escalated).
 */
const OFF = "off\\b(?!-)";
/**
 * What may follow a BARE figure and "off" for it to read as money: a clause
 * end, a connective, or a price noun. "half off since it's a gift" and "50
 * off the total" are money; "2 off the shelf rings" is not.
 */
const PRICE_NOUN = "(?:price|total|bill|cost|quote|tag|sticker|order|invoice|purchase)";
const OFF_TAIL =
  "(?=\\s*(?:[.?!,;:)]|$|(?:for|on|if|since|because|please|pls|plz|and|or|then|would|could)\\b|" +
  `(?:the|my|your|this|that|our)\\s+${PRICE_NOUN}\\b))`;

/** "20% off", "forty dollars off", "20-percent-off": a unit makes it money, whatever follows. */
const FIGURE_OFF = new RegExp(`${FIGURE_WITH_UNIT}${GAP}${OFF}`, "i");
/** "half off since it's a gift", "can you do 50 off?": no unit, so the tail must say money. */
const BARE_OFF = new RegExp(`(?:\\b\\d[\\d,.]*|\\b(?!one\\b)${SPELLED})${GAP}${OFF}${OFF_TAIL}`, "i");
/** "15% less", "twenty percent cheaper". A unit is required, so "2 less" is not one. */
const FIGURE_LESS = new RegExp(`${FIGURE_WITH_UNIT}${GAP}(?:less|cheaper|lower)\\b`, "i");
const DISCOUNT_WORD = /\bdiscount\w*/i;
const PRICE_MATCH = /\bprice[\s-]*match\w*/i;
const PROMO = /\b(?:coupon|promo)(?:\s*codes?|s)?\b/i;

/** "$50 off": the amount is found by figures.ts (F-16), never by a money regex here. */
function moneyOff(text: string): boolean {
  let from = 0;
  for (const token of extractMoney(text)) {
    const at = text.indexOf(token, from);
    if (at < 0) continue;
    from = at + token.length;
    if (new RegExp(`^${GAP}${OFF}`, "i").test(text.slice(from))) return true;
  }
  return false;
}

/** A bare "match that" or "beat it" counts only at a clause end or before one of these. */
const MATCH_TAIL = "(?=\\s*(?:[.?!,]|$|for\\s+(?:me|us)\\b|too\\b|as\\s+well\\b|price|deal|offer|quote))";
/** "match their price", "beat the online quote", "match the sale". Always a price ask. */
const MATCH_PRICE =
  /\b(?:match|beat)\s+(?:the|their|his|her|your|that|this|a|any)\s+(?:(?:lower|cheaper|better|same|other|online|sale)\s+)?(?:price|deal|offer|quote|discount|sale)\b/i;
/** "beat that for me". A jeweler is asked to beat a price, not a design. */
const BEAT_BARE = new RegExp(`\\bbeat\\s+(?:that|this|it|them|theirs)${MATCH_TAIL}`, "i");
/**
 * "can you match it?" is a price match only when the line talks money
 * (TA-384 repair): "I love this ring from pinterest, can you match it?" is a
 * design ask, "it's listed lower elsewhere, can you match it?" is not.
 */
const MATCH_BARE = new RegExp(`\\bmatch\\s+(?:that|this|it|them|theirs|hers|his)${MATCH_TAIL}`, "i");
const PRICE_CONTEXT =
  /%|\b(?:prices?|priced|pricing|cheaper|lower|less|quot(?:e|ed|es)|listed|sale|deal|paid|charged|costs?|bucks|dollars|percent|discount\w*)\b|\bfor\s+\d/i;
const HONOUR_ASK =
  /\bhonou?r\s+(?:that|this|the|your|their|his|her|my)\s+(?:(?:old|original|earlier|same|other)\s+)?(?:price|quote|deal|discount|offer|rate)\b/i;
/**
 * "knock something off", "could you take a little off?", "shave a bit off the
 * price". A small amount "off" a price noun is money whatever the verb; with no
 * object, only the price idioms (knock, take, drop) count, because "cut some
 * off the chain" and "take a little off the band" are bench work (TA-384 repair).
 */
const SMALL_AMOUNT = "(?:a\\s+(?:bit|little|touch)|some(?:thing)?|anything)";
const TAKE_OFF = new RegExp(
  `\\b(?:knock|take|drop|shave|chop|cut)\\s+${SMALL_AMOUNT}\\s+${OFF}\\s+(?:the|my|your|this|that|our)\\s+${PRICE_NOUN}\\b|` +
    `\\b(?:knock|take|drop)\\s+${SMALL_AMOUNT}\\s+${OFF}(?=\\s*(?:[.?!,;:]|$|for\\s+(?:me|us)\\b|please\\b|pls\\b))`,
  "i",
);

/** Someone other than the customer, named as a person, never a pronoun ("do they get cheaper"). */
const THIRD_PARTY =
  "(?:friends?|buddy|pal|bestie|sisters?|brothers?|mom|mum|mother|dad|father|cousins?|aunt(?:ie)?|" +
  "uncle|wife|husband|partner|girlfriend|boyfriend|fiancee?|co-?workers?|colleagues?|neighbou?rs?|" +
  "room-?mates?|boss|son|daughter|niece|nephew|grand(?:ma|pa|mother|father)|someone|somebody|" +
  "another\\s+customer|other\\s+customers|everyone\\s+else|other\\s+people)";
const RECEIVED = "(?:got|gets|getting|received|paid|pays|scored|was\\s+(?:given|offered|charged|quoted))";
/**
 * What they got, when it is a price concession. A percent counts only as a
 * saving ("20 percent off", "a 20% reduction"), never as a composition: "a
 * ring that is 75% gold" is a purity question (TA-384 repair). "the day off"
 * and "for free" are not concessions (see FINDINGS TA-384).
 */
const CONCESSION_OBJECT =
  `(?:(?:%|\\b(?:percent|per${GAP}cent|perc|pct))${GAP}(?:off|discount\\w*|less|cheaper|lower|reduction|markdown|savings?|taken|knocked|back)\\b|` +
  "\\b(?:discount\\w*|money\\s+off|for\\s+less|less\\s+than\\s+(?:me|i|that|what|us)|" +
  "cheaper|lower\\s+price|(?:a|better)\\s+deal|better\\s+(?:price|rate)|same\\s+(?:price|deal|discount|rate)|reduction|markdown)\\b)";
/** "my cousin got twenty percent off", "a colleague paid less than me". One sentence, in that order. */
const SOCIAL_PROOF = new RegExp(
  `\\b${THIRD_PARTY}\\b[^.?!]{0,60}?\\b${RECEIVED}\\b[^.?!]{0,60}?${CONCESSION_OBJECT}`,
  "i",
);

/** A third party and what they received, the start of a price-paid claim. */
const SOCIAL_RECEIPT = new RegExp(`\\b${THIRD_PARTY}\\b[^.?!]{0,60}?\\b${RECEIVED}\\b`, "i");
/**
 * A price someone else paid, written without a "$": "250 dollars", "three
 * hundred bucks", "for 1500", or digits straight after "paid". A "$" amount
 * comes from figures.ts instead (F-16).
 */
const WORD_PRICE = new RegExp(
  `(?:\\b\\d[\\d,.]*|\\b${SPELLED})${GAP}(?:dollars?|bucks?|usd)\\b|\\bfor\\s+\\d[\\d,.]*|^\\s*\\d[\\d,.]*\\b`,
  "i",
);
/**
 * Asking for that price: "can I get that price?", "can I have it for that?",
 * "I'd like the same", "why is mine $65?", "can you do it for that?".
 * "the same" counts only at a clause end or before "for me", so "is that the
 * same for a thicker band?" is a price question, not an ask for their price.
 */
const PRICE_ASK = new RegExp(
  "\\b(?:(?:can|could|will|would)\\s+(?:i|we|you)\\s+(?:get|have|pay|do)\\s+(?:it\\s+|that\\s+|one\\s+)?(?:for\\s+)?(?:that|the\\s+same)\\b|" +
    "for\\s+that\\b|that\\s+(?:price|rate|deal|amount)\\b|" +
    "why\\s+(?:is|am|are|does|do)\\s+(?:mine|my|i|it|this|yours|you)\\b|match\\b|honou?r\\b)|" +
    "\\bsame(?=\\s*(?:[.?!,]|$|for\\s+(?:me|us)\\b|price|deal|rate|too\\b))",
  "i",
);

/**
 * TA-384 repair. "my friend paid $900 for this ring, can I have it for
 * that?". The brief names dollars beside percent; the first cut read only a
 * percent or a discount word after a third party. Three parts, in order: a
 * named third party received something, a price in the same sentence, then an
 * ask for that price anywhere after it. A price mentioned with no ask ("my
 * friend paid $300 for a ring here, do you have similar ones?") is not one.
 */
function socialProofPrice(text: string): boolean {
  const receipt = SOCIAL_RECEIPT.exec(text);
  if (receipt === null) return false;
  const rest = text.slice(receipt.index + receipt[0].length);
  const sentenceEnd = rest.search(/[.?!](?=\s|$)/);
  const sentence = sentenceEnd < 0 ? rest : rest.slice(0, sentenceEnd);
  let priceEnd = -1;
  const money = extractMoney(sentence)[0];
  if (money !== undefined) priceEnd = sentence.indexOf(money) + money.length;
  const word = WORD_PRICE.exec(sentence);
  if (word !== null) {
    const end = word.index + word[0].length;
    priceEnd = priceEnd < 0 ? end : Math.min(priceEnd, end);
  }
  if (priceEnd < 0) return false;
  return PRICE_ASK.test(rest.slice(priceEnd));
}

/*
 * Z2 CLAIMSCREEN (Y6 ASI01, ASI06; CLAUDE.md section 6). The customer-side
 * vocabulary of a rate said without "off", the shape a planted memory note
 * takes ("the owner agreed my resizes are under list", "the family rate comes
 * off as a percentage"). Read for a CUSTOMER's words only (`isConcessionRequest`
 * gates on the author), so owner and KB text is never read by it, and
 * `statesStandingOffer` (the KB side, `KB_PROMO_PATTERNS`) is untouched. A hit
 * is a QUARANTINE of one recalled row or an escalation of one line, never a
 * refusal of the customer. AWAITING RATIFICATION: the closed word lists, a
 * first cut not calibrated on real traffic.
 */
/** "under list", "below retail", "under the list price": a rate set against the list. */
const UNDER_LIST = /\b(?:under|below|beneath)\s+(?:the\s+)?(?:list|retail|msrp|sticker)(?:\s+price)?\b/i;
/** A figure in the same sentence, digit or spelled: "is the price under 500" has no list word and never reaches this. */
const SENTENCE_FIGURE = new RegExp(`\\d|\\b${NUMBER_WORD}\\b`, "i");
function underListWithFigure(text: string): boolean {
  return text.split(/[.?!;\n]+/).some((s) => UNDER_LIST.test(s) && SENTENCE_FIGURE.test(s));
}
/** "family rate", "friends and family rate", "the staff rate": a standing rate for a kind of person. */
const KIND_RATE = /\b(?:family|friends?|employee|staff|insider|trade)\s+rates?\b/i;
/** "comes off as a percentage", "is taken off as a percent": a rule for taking money off. */
const OFF_AS_PERCENT = /\b(?:comes?|taken|is\s+taken|gets?\s+taken|are\s+taken)\s+off\s+as\s+a\s+percent(?:age)?\b(?!\s+of\s+(?:the\s+|its\s+|their\s+)?(?:weight|gold|metal|stone|diamond|carat|size|mass))/i;
/**
 * "the special rate for me is 15", "the usual rate was twenty": a named rate, a
 * copula and a figure. Closed modifier list, so "the interest rate", "the flat
 * rate", "the exchange rate" and "the rate of return" never reach it, and a
 * window that talks financing, tax or shipping is excluded outright.
 * AWAITING RATIFICATION: the 50 character window between the rate word and the copula.
 */
const NAMED_RATE = new RegExp(
  `\\bthe\\s+(?:special|usual|regular|standard|loyalty|member|vip|preferred|wholesale|owner|old|agreed|negotiated|promised|reduced|my)\\s+rate\\b[^.?!]{0,50}?\\b(?:is|was|are|were)\\s+(?:about\\s+|around\\s+|only\\s+|just\\s+)?(?:\\d|${NUMBER_WORD}\\b)`,
  "i",
);
const NOT_A_PRICE_RATE = /\b(?:financ\w*|interest|apr|loan|mortgage|installments?|tax|shipping|exchange|return)\b/i;
function claimsAKindOfRate(text: string): boolean {
  if (underListWithFigure(text) || KIND_RATE.test(text) || OFF_AS_PERCENT.test(text)) return true;
  return text.split(/[.?!;\n]+/).some((s) => NAMED_RATE.test(s) && !NOT_A_PRICE_RATE.test(s));
}

/** The pattern half of the reader: every way a line asks, testable one by one. */
const CONCESSION_REQUEST_PATTERNS: readonly RegExp[] = [
  DISCOUNT_WORD,
  PRICE_MATCH,
  FIGURE_OFF,
  BARE_OFF,
  FIGURE_LESS,
  PROMO,
  MATCH_PRICE,
  BEAT_BARE,
  HONOUR_ASK,
  TAKE_OFF,
  SOCIAL_PROOF,
];

/**
 * Compatibility forms (fullwidth digits and a fullwidth percent sign, U+FF05) read as their plain
 * forms, so a keyboard's choice of glyph does not open the gate (TA-384 repair).
 */
function plain(text: string): string {
  return text.normalize("NFKC");
}

/**
 * TA-384. Does this customer line ask for money off, a match, or what someone
 * else paid or got? The reply path answers such a line only from a KB fact
 * that `statesStandingOffer`; with none admitted it takes the A3.6 no-answer
 * path and escalates. A miss here leaves the line to the ordinary gates; a
 * false hit sends it to a human, which is the graceful direction (section 0).
 *
 * DISC fix round 5: `opts.author` names who wrote `text`, never defaulted. A
 * customer's line (the retrieval gate, the memory screen) is read as base
 * e5faa94 read it; a shop's fixed line (`checkFixedLine`) by every reader.
 */
export function isConcessionRequest(text: string, opts: DiscountReadOptions): boolean {
  const line = plain(text);
  return (
    CONCESSION_REQUEST_PATTERNS.some((p) => p.test(line)) ||
    moneyOff(line) ||
    // Decision list item 125 (rec accepted 2026-10-02): a fraction off ("can I
    // get it half price?", "a quarter off?") is read by figures.ts, which holds
    // the fraction words; a hit here is the hold direction.
    extractDiscountClaims(line, opts).length > 0 ||
    (MATCH_BARE.test(line) && PRICE_CONTEXT.test(line)) ||
    socialProofPrice(line) ||
    // Z2: the customer's own claimed rate. Customer words only; a shop line is read by its own readers.
    (opts.author === "customer" && claimsAKindOfRate(line))
  );
}

/**
 * TA-384. Does this KB fact state a standing price concession the shop has
 * written down: a discount (including "no discounts"), a price match, a
 * figure off? Only the declarative half of the vocabulary above. A freebie
 * term of sale ("free cleaning for life") is not one: it does not answer a
 * request for money off, and answering with it is the non-sequitur the red
 * team's social-proof attack was written to catch.
 */
export function statesStandingOffer(fact: string): boolean {
  const text = plain(fact);
  return [DISCOUNT_WORD, PRICE_MATCH, FIGURE_OFF, BARE_OFF, PROMO].some((p) => p.test(text)) || moneyOff(text);
}

/*
 * AWAITING RATIFICATION. The long-run discount ceiling for one conversation.
 * `MAX_CONVERSATION_DISCOUNT_PCT` is 0 for agent-drafted messages by Kabir's
 * decision 3 of the 10-02b list, until he ratifies a limit. Whether a drafted
 * reply may ever concede a discount, and how much, is his (T3, CLAUDE.md
 * section 3). The vault gives 5% as an example of a single goodwill step and
 * never states a ceiling, so no number is proposed here.
 */

/**
 * The running total. Sized discounts add; freebies count, because they have no
 * size; money off ("$50 off") counts apart, because its size as a percent is
 * not known and adding a guess would give a cap a number nobody stated.
 */
export function tallyConcessions(all: readonly Concession[]): ConcessionTally {
  let discountPercent = 0;
  let freebies = 0;
  let unsizedDiscounts = 0;
  for (const c of all) {
    if (c.kind === "freebie") freebies += 1;
    else if (c.percent === null) unsizedDiscounts += 1;
    else discountPercent += c.percent;
  }
  return { discountPercent, freebies, unsizedDiscounts };
}

/** The cap as a breach detail says it. */
const DISCOUNT_CAP_WORDS = `the ${MAX_CONVERSATION_DISCOUNT_PCT}% cap a drafted reply is held to`;

/**
 * Null when the conversation is still inside both caps.
 *
 * At the cap is allowed and over it is not: a cap the total may not reach is a
 * different number than the one written down, and the difference would only
 * ever be discovered by a customer being told no at the figure the sheet names.
 * Money off of no stated percent cannot be shown to sit inside any cap, so one
 * is over it whatever the cap is: the hold direction (decision 3, 10-02b).
 */
export function exceedsConcessionCaps(t: ConcessionTally): ConcessionBreach | null {
  if (t.unsizedDiscounts > 0) {
    const amounts = t.unsizedDiscounts === 1 ? "an amount" : `${t.unsizedDiscounts} amounts`;
    const besides = t.discountPercent > 0 ? ` and ${t.discountPercent}% besides` : "";
    return {
      cap: "discount",
      detail:
        `this conversation has now conceded ${amounts} off with no stated percent${besides}, ` +
        `a size that cannot be shown to sit inside ${DISCOUNT_CAP_WORDS}`,
    };
  }
  if (t.discountPercent > MAX_CONVERSATION_DISCOUNT_PCT) {
    return {
      cap: "discount",
      detail: `this conversation has now conceded ${t.discountPercent}% in total, over ${DISCOUNT_CAP_WORDS}`,
    };
  }
  if (t.freebies > MAX_CONVERSATION_FREEBIES) {
    return {
      cap: "freebies",
      detail:
        `this conversation has now included ${t.freebies} ${t.freebies === 1 ? "item" : "items"} at no charge, ` +
        `over the cap of ${MAX_CONVERSATION_FREEBIES} a drafted reply is held to`,
    };
  }
  return null;
}

/**
 * TA-571: "the rush fee is waived" and "rush fee waived" are one waiver, so both compare as
 * "rush fee waived". Fix round 1: leading filler ("of course", "and as always", "yes") is
 * dropped too, so the key starts at the fee or service the waiver names.
 * AWAITING RATIFICATION: the filler list is the builder's, not Kabir's.
 */
const WAIVER_KEY_FILLER = [
  "the", "your", "our", "any", "all", "a", "an", "my", "this", "that", "his", "her", "their",
  "so", "yes", "ok", "okay", "sure", "of", "course", "and", "but", "or", "plus", "also", "as",
  "always", "again", "still", "even", "now", "today", "then", "well", "oh", "hi", "hey", "great",
  "good", "news", "happily", "gladly", "absolutely", "certainly", "naturally", "definitely", "indeed",
  "please", "i", "we", "you", "he", "she", "they", "it", "us", "me",
].join("|");
function waiverKey(text: string): string {
  return text
    .replace(/\b(?:is|are|was|were|be|been)\s+waived\b/g, "waived")
    .replace(new RegExp(`^(?:(?:${WAIVER_KEY_FILLER})\\s+)+`), "");
}

/** A waiver key with no word left before "waived" names nothing, so no fact grounds it (fix round 1). */
function isGroundedWaiver(key: string, statesInWaivers: (text: string) => boolean): boolean {
  return /[\w-]+\s+waived$/.test(key) && statesInWaivers(key);
}

/**
 * WAIVE3 (decision 124): a fact grounds a freebie by stating it, and a waiver only where
 * the fact does not refuse it. "No rush fee waived on sale items." and "we do not waive
 * the rush fee" state a refusal, so neither grounds "Rush fee waived for you!" or "I'll
 * waive the rush fee": the fact side is read by the same governed-negation reader as the
 * draft. A fact that states the waiver elsewhere still grounds it.
 */
function statesFreebie(facts: string, text: string): boolean {
  if (!/\bwaiv/.test(text)) return facts.includes(text);
  for (let at = facts.indexOf(text); at !== -1; at = facts.indexOf(text, at + 1)) {
    if (!isRefusedWaiver(facts, at + text.length)) return true;
  }
  return false;
}

/** `statesFreebie` against fixed facts, remembered per distinct text (row 214(a)), so a repeated draft match scans the facts once; the pure function stays pure. */
function memoStatesFreebie(facts: string): (text: string) => boolean {
  const stated = new Map<string, boolean>();
  return (text) => {
    let hit = stated.get(text);
    if (hit === undefined) stated.set(text, (hit = statesFreebie(facts, text)));
    return hit;
  };
}

/**
 * TA-571 fix round 1: "no", "nothing", "none", "isn't" and "aren't" refuse a waiver only
 * when they govern it, sitting right before the fee it waives or its verb ("no fee is
 * waived", "nothing is waived", "none waived", "isn't waived"). Anywhere else in the
 * clause they are something else: "no rush and the fee is waived" is a waiver.
 * WAIVE3 (decision 123): "extra", "additional", "added" and "hidden" after "no" open the
 * "no extra cost" assurance, never the fee a refusal names, so "No extra cost sizing is
 * waived." is a waiver. "No extra fees are waived." counts too: the hold direction.
 * AWAITING RATIFICATION: the word lists are the builder's, not Kabir's.
 */
const WAIVER_AUX = "(?:is|are|was|were|be|been|being|will|would|can|could|ever|get|gets|got|has|have|had)";
const WAIVER_NOT_SUBJECT =
  "(?:and|or|but|so|because|as|since|while|when|if|at|all|then|plus|with|the|a|an|your|our|my|this|that|needed|required|necessary|worries|worry|problem|problems|need|doubt|trouble|hurry|stress|pressure|extra|additional|added|hidden)";
const WAIVER_GOVERNED_REFUSAL = new RegExp(
  `\\b(?:` +
    `(?:isn['\u2019]?t|aren['\u2019]?t)\\s+(?:${WAIVER_AUX}\\s+){0,2}(?:waived|waiv(?:e|es|ing)\\s+(?:(?:the|your|any|our|all)\\s+)?[\\w-]+)` +
    `|(?:nothing|none)(?:\\s+of\\s+(?:it|them|that|this|these|those|(?:the|our|your)\\s+[\\w-]+))?\\s+(?:${WAIVER_AUX}\\s+){0,3}waived` +
    `|no\\s+(?:(?!${WAIVER_NOT_SUBJECT}\\b)[\\w-]+\\s+){1,3}(?:${WAIVER_AUX}\\s+){0,3}waived` +
  `)$`,
  "i",
);

/**
 * WAIVE3 (decision 112): a base negator refuses a waiver only when it governs the waiver
 * verb, the way "no" does above: in the same clause, right before the verb, with at most
 * three of these words between ("won't be waived", "unable to waive", "can't get it
 * waived", "cannot ever be waived"). The list holds no conjunction and no noun, so "Not
 * to worry and the fee is waived" is a waiver: the "not" governs "worry", not the verb.
 * AWAITING RATIFICATION: the word list is the builder's, not Kabir's.
 */
const WAIVER_NEGATOR_GAP =
  "(?:is|are|was|were|be|been|being|will|would|can|could|ever|get|gets|got|has|have|had|do|does|did|able|to|it|them|that|this|currently|usually|normally|typically|really|actually|just|simply|also|going|possibly)";
const WAIVER_GOVERNING_NEGATOR = new RegExp(
  `\\b(?:cannot|can['\u2019]?t|won['\u2019]?t|wont|don['\u2019]?t|dont|doesn['\u2019]?t|(?<!\\bwhy\\s+)not(?!\\s+(?:just|simply|merely|only)\\b)|never|unable)\\s+(?:${WAIVER_NEGATOR_GAP}\\s+){0,3}$`,
  "i",
);
/** Both refusal readers are end-anchored windows, so a clause is read no further back than this (linear per call; the fact side is memoised per distinct text, row 214(a)). */
const WAIVER_CLAUSE_MAX = 160;

/**
 * TA-566: "cannot be waived" and "we don't waive it" refuse a waiver, so they are no freebie.
 * TA-571: so do "no fee is waived" and "nothing is waived". WAIVE3: a base negator refuses
 * only when it governs the waiver verb, the last "waiv" in the clause ending at `end`.
 */
function isRefusedWaiver(reply: string, end: number): boolean {
  let from = end;
  const floor = Math.max(0, end - WAIVER_CLAUSE_MAX);
  while (from > floor && !".;!?,".includes(reply[from - 1] ?? "")) from -= 1;
  const clause = reply.slice(from, end);
  const verb = clause.toLowerCase().lastIndexOf("waiv");
  return (verb !== -1 && WAIVER_GOVERNING_NEGATOR.test(clause.slice(0, verb))) || WAIVER_GOVERNED_REFUSAL.test(clause);
}

/*
 * TA-604 OFFERFB2 (loop i, rebuild of OFFERFB per decision 168). Ruling 53 at
 * the KB side, in the two tiers of decision 163. A PROMO (tier 2: money off, a
 * sale, a code, a BOGO, a markdown, "save $N", "spend $X and receive", a
 * trade-in percentage, anything time-boxed) never reaches the reply agent on a
 * question that does not ask about offers. A SERVICE POLICY (tier 1: free
 * shipping, resizing, engraving, cleaning, returns, consultations, parking, a
 * warranty) is the shop's value proposition, not a discount, and stays.
 *
 * Read per SENTENCE, so a chunk that states a price and a promo keeps its price
 * (decision 163: a price question is never answered without its price, and
 * never escalated because of a promo). CLAUDE.md section 0.5: a gate that holds
 * an offer is working, so a miss here leaves a promo to the verifier and the
 * concession ledger, never to the customer unread.
 *
 * AWAITING RATIFICATION (TA-604): every list below is the builder's first cut,
 * written against synthetic probes (retrieval.probe.loopi.test.ts), not
 * calibrated on real traffic. Each stays under 25 entries; a probe that would
 * need more is a known miss, not a longer list.
 */

/**
 * KB-side promo shapes, one per line. Regex shapes only: never the bare words
 * credit, membership, member, offer or deal, which a policy uses all the time
 * ("store credit", "membership in Jewelers of America", "we offer custom
 * design", "how we deal with a lost stone"). A hit preceded in its clause by a
 * negator ("we don't discount", "no discounts") is a refusal, not a promo.
 * AWAITING RATIFICATION (TA-604).
 */
/**
 * `FIGURE_WITH_UNIT` with a start guard: a figure never starts inside another
 * figure, so a run of "1,1,1" is tried once, not once per digit (fix round 1:
 * 168 ms at 10k characters unguarded, quadratic). The same shape on customer
 * text (`FIGURE_WITH_UNIT` itself, base) is left for Kabir.
 */
const KB_FIGURE_WITH_UNIT = `(?:(?<![\\d,.])\\b\\d[\\d,.]*${UNIT}|\\b${SPELLED}${UNIT})`;

export const KB_PROMO_PATTERNS: readonly RegExp[] = [
  // "15% off", "a 10 percent reduction", "twenty percent off", "15% below list".
  new RegExp(`${KB_FIGURE_WITH_UNIT}${GAP}(?:${OFF}|less\\b|cheaper\\b|reduction|discount\\w*|markdown|savings?\\b|below\\b)`, "gi"),
  // "a military discount", "discounted bands"; "we don't discount" is negated.
  /\bdiscount(?:s|ed)?\b/gi,
  // "we price match any local jeweler".
  /\bprice[\s-]*match\w*/gi,
  // "on sale", never "final sale".
  /\bon\s+sale\b/gi,
  // A named sale: "holiday sale", "spring sale", "anniversary sale".
  /\b(?:holiday|spring|summer|fall|autumn|winter|anniversary|annual|seasonal|flash|weekend|christmas|sample|warehouse|semi-annual|end[\s-]of[\s-]season|friday|monday|day)\s+sales?\b/gi,
  // "sale ends Sunday", "sale price": a sale with an end or a price.
  /\bsales?\s+(?:ends?|ending|starts?|runs?|prices?|pricing)\b/gi,
  // "clearance on last season's bracelets".
  /\bclearance\b/gi,
  // "promo code GOLD", "a promotion", "use coupon RING10", "with code OVAL10".
  /\b(?:promo(?:tion(?:al)?)?s?|coupons?)\b|\b(?:use|with|enter)\s+(?:the\s+)?code\b/gi,
  // "buy one, get one", "buy a pair ... and get the second", "BOGO".
  /\bbogo\b|\bbuy\s+(?:one|1|a|an|two|2)\b[^.;]{0,40}?\bget\b/gi,
  // "the second pair half price", "half off".
  /\bhalf[\s-]+(?:price|off)\b/gi,
  // "marked down by 25 percent", "markdowns".
  /\bmark(?:ed|s)?[\s-]*downs?\b/gi,
  // "save $100", "save 10%", "save up to twenty percent".
  new RegExp(`\\bsav(?:e|es|ing)\\s+(?:up\\s+to\\s+|an?\\s+(?:extra|additional)\\s+)?(?:\\$|\\d|${SPELLED}\\b)`, "gi"),
  // KB_SPEND (index 12): "spend $1,000 and receive", "when you spend $500 or more"; yields to a free service (decision 181).
  /\bspend(?:s|ing)?\s+(?:[\w,]+\s+){0,2}?(?:\$|\d)/gi,
  // "trade in an old bracelet and we credit 120% of its value": a trade-in percentage.
  new RegExp(`\\btrade[\\s-]*ins?\\b[^.;]{0,60}?${KB_FIGURE_WITH_UNIT}`, "gi"),
  // "limited-time", "this week only", "while supplies last", "for a limited time".
  /\blimited[\s-]+(?:time|period|edition\s+offer)\b|\b(?:today|tonight|this\s+(?:week(?:end)?|month))\s+only\b|\bwhile\s+(?:supplies|stocks?)\s+lasts?\b/gi,
  // "a gift with every engagement ring purchase", "a free gift", "a free upgrade", "bonus".
  /\bgifts?\s+with\s+(?:(?:every|each|any|your|a|the)\s+)?(?:[\w-]+\s+){0,3}?purchases?\b|\bfree\s+(?:gifts?\b(?!\s*(?:box|wrap|bag|packag|card|message|note))|upgrades?\b)|\bbonus\b/gi,
  // "we throw in a gold chain".
  /\b(?:throw(?:s|ing)?|thrown)\s+in\b/gi,
  // Segment pricing: "friends and family rate", "preferred pricing", "VIP prices", "introductory offer".
  /\b(?:friends?\s+(?:and|&)\s+family|family\s+(?:and|&)\s+friends|preferred|insider|members?['’]?|vip|loyalty|student|military|veterans?|teachers?|seniors?|employee|first[\s-]*time(?:\s+(?:buyer|customer|client)s?)?|special|exclusive|introductory|courtesy)\s+(?:pricing|prices?|rates?|discounts?|offers?|deals?)\b/gi,
  // "a courtesy on any strand".
  /\ba\s+courtesy\s+(?:on|of)\b/gi,
  // "loyalty program", "reward points", "earn points", "refer a friend".
  /\b(?:loyalty|rewards?|referral)\s+(?:program(?:me)?s?|points?|club|card|scheme|bonus|credit)\b|\b(?:earn|collect)\s+(?:[\w-]+\s+)?points\b|\brefer\s+a\s+friend\b/gi,
  // "priced below retail", "under list price", "less than MSRP".
  /\b(?:below|under|beneath|less\s+than)\s+(?:the\s+)?(?:list|retail|msrp|rrp)(?:\s+prices?)?\b/gi,
  // "cash back".
  /\bcash[\s-]*back\b/gi,
];

/**
 * Words that make a sentence gratis. A gratis sentence is a SERVICE POLICY
 * (tier 1) unless it is time-boxed (`KB_TIME_BOX`), which makes it a promo.
 * "on us" counts only as "is/are on us", so "you can count on us" is not one.
 * AWAITING RATIFICATION (TA-604).
 */
export const KB_GRATIS: readonly RegExp[] = [
  // "free shipping", "free of charge" (the KB_NOT_GRATIS shapes are removed first).
  /\bfree\b/i,
  // "complimentary cleaning".
  /\bcomplimentary\b/i,
  // "no charge", "no extra cost", "no fee".
  /\bno\s+(?:extra\s+|additional\s+)?(?:charge|cost|fee)s?\b/i,
  // "at no cost".
  /\bat\s+no\s+(?:extra\s+|additional\s+)?(?:cost|charge)\b/i,
  // "engraving is on us", "on the house"; never "count on us".
  /\b(?:is|are|['’]s|be|been)\s+on\s+(?:us|the\s+house)\b/i,
  // "the resizing fee is waived".
  /\bwaiv(?:e|es|ed|ing)\b/i,
  // "without charge".
  /\bwithout\s+(?:charge|cost)\b/i,
];

/**
 * "free" that does not mean gratis, removed before `KB_GRATIS` reads a
 * sentence: "free of nickel", "free from dyes", "duty free", "conflict-free",
 * "hands free", "feel free", "free text". Distinct from the draft-side
 * `NOT_GRATIS` above, which reads a reply, not a KB fact.
 * AWAITING RATIFICATION (TA-604).
 */
export const KB_NOT_GRATIS: readonly RegExp[] = [
  // "free of nickel", "free from dyes"; "free of charge" stays gratis.
  /\bfree\s+(?:of|from)\b(?!\s+charge)/gi,
  // "duty free", "nickel-free", "conflict-free", "hands free", "worry-free".
  /\b(?:duty|nickel|lead|conflict|cadmium|allergen|hands|tarnish|hassle|worry|risk|stress|scratch|maintenance|cruelty|tax)[\s-]+free\b/gi,
  // "feel free to stop by".
  /\bfeel\s+free\b/gi,
  // "a free text field", "free-form", "free-standing", "freehand".
  /\bfree[\s-]*(?:text|form|standing|flowing|hand)\b/gi,
];

/** A gratis sentence becomes a promo inside one of these: "this month", "through December 31", "until Sunday". AWAITING RATIFICATION (TA-604). */
const KB_TIME_BOX = new RegExp(
  "\\bthis\\s+(?:week(?:end)?|month|season|spring|summer|fall|autumn|winter|holiday(?:\\s+season)?)\\b|" +
    "\\b(?:through|thru|until|till|ends?|ending)\\s+(?:the\\s+end\\s+of\\s+|the\\s+)?" +
    "(?:jan(?:uary)?|feb(?:ruary)?|mar(?:ch)?|apr(?:il)?|may|june?|july?|aug(?:ust)?|sep(?:t(?:ember)?)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?|" +
    "monday|tuesday|wednesday|thursday|friday|saturday|sunday|tomorrow|tonight|month|week(?:end)?|\\d)",
  "i",
);

/** A refusal in the same clause, before the promo word: "we don't discount", "no discounts". */
const KB_NEGATOR = /\b(?:no|not|never|none|don['’]?t|do\s+not|doesn['’]?t|won['’]?t|cannot|can['’]?t|without)\b/i;

function negatedBefore(text: string, at: number): boolean {
  let from = at;
  while (from > 0 && !",;:.!?".includes(text[from - 1] ?? "")) from -= 1;
  return KB_NEGATOR.test(text.slice(from, at));
}

/**
 * A refusal right after the promo word, in its clause: "Discounts are not
 * offered on bespoke pieces", "promotional pricing does not apply". One word
 * may sit between ("pricing"); a bare "no" or "without" after the word does
 * not count ("10% off with no minimum" is still an offer).
 */
const KB_NEGATED_AFTER =
  /^\s+(?:[\w'’-]+\s+)?(?:(?:is|are|was|were|will|do|does|did|can|could|may|would|should|shall)\s+(?:not|never)\b|(?:is|are|was|were|wo|ca|do|does|did|could|would|should)n['’]?t\b|cannot\b|never\b|no\s+longer\b)/i;

function refused(text: string, at: number, end: number): boolean {
  return negatedBefore(text, at) || KB_NEGATED_AFTER.test(text.slice(end));
}

/**
 * Fix round 1 item 1, narrowed in fix round 2. A sentence that carries one of
 * these terms is a returns, exchange, refund, warranty or final-sale POLICY,
 * and inside it the item-class sale words (`KB_ITEM_CLASS_SALE`) name what the
 * policy covers: "except clearance", "items bought on sale can be exchanged",
 * "promotional items cannot be returned". Only those words are masked; every
 * other promo shape (a time box, BOGO, save, loyalty, bonus, price match, a
 * named sale, a figure off) and the gratis-without-service rule stay live, so
 * "Warranty holders save 15% on repairs" is still a promo. "return customers"
 * is not a returns term. AWAITING RATIFICATION (TA-604).
 */
const KB_POLICY_TERMS =
  /\b(?:returns?(?!\s+(?:customers?|clients?|visitors?|shoppers?|buyers?)\b)|returned|returnable|non[\s-]*returnable|exchang\w*|refund\w*|warrant\w*|guarantee\w*|final[\s-]+sale)\b/i;

/** The item-class sale words a policy sentence may name: "on sale", "clearance", "sale items", "promotional" or "discounted" pieces, "final sale". AWAITING RATIFICATION (TA-604). */
const KB_ITEM_CLASS_SALE =
  /\b(?:on\s+(?:sale|clearance)|clearance|promotional|discounted|sale(?=\s+(?:and|or|&)\s+clearance\b)|sale\s+(?:items?|pieces?|merchandise|goods)|final[\s-]+sale)\b/gi;

/**
 * Decision 181 (fix round 2, P30): "spend $X" yields when what is free is a
 * service ("Insured shipping is free when you spend $500" is a standing
 * shipping policy, the same as "free on orders over $500"). Goods named after
 * a service word ("a complimentary cleaning kit") are not the service, so
 * "spend $5000 and get a complimentary cleaning kit" stays a promo.
 * AWAITING RATIFICATION (TA-604).
 */
const KB_SPEND = 12;
const KB_SERVICE_GOODS = /\b[\w-]+\s+(?:kits?|sets?|cloths?|cards?|vouchers?|certificates?|products?|supplies)\b/gi;

/**
 * SERVICE_FAMILIES: the services a tier 1 policy is about, read from a
 * question or a sentence. Fix round 1 item 3: a gratis sentence is a service
 * policy only when it names one of these (in the sentence, else in the chunk's
 * title and summary); "a free pair of studs with any engagement ring" names
 * none and is a promo. Moved here from retrieval.ts so the sentence reader can
 * use it (retrieval.ts re-exports it). Fix round 1 added servicing and reset
 * (repair) and plating and rhodium (cleaning). AWAITING RATIFICATION (TA-604).
 */
export const SERVICE_FAMILIES: readonly (readonly [string, RegExp])[] = [
  // Shipping and delivery.
  ["shipping", /\b(?:ship\w*|deliver\w*|postage|courier)\b/i],
  // Resizing, sizing, adjustment.
  ["resizing", /\b(?:re)?siz(?:e|es|ed|ing)\b|\badjust\w*/i],
  // Engraving.
  ["engraving", /\bengrav\w*/i],
  // Cleaning, polishing, inspection, plating.
  ["cleaning", /\b(?:clean\w*|polish\w*|inspect\w*|plat(?:e|ed|ing)\b|rhodium)/i],
  // Returns, exchanges, refunds.
  ["returns", /\b(?:return\w*|exchang\w*|refund\w*)/i],
  // Warranty and guarantee.
  ["warranty", /\b(?:warrant\w*|guarantee\w*)/i],
  // Consultations and appointments.
  ["consultation", /\b(?:consult\w*|appointments?)\b/i],
  // Parking.
  ["parking", /\bpark(?:ing)?\b/i],
  // Gift wrap, boxes, packaging.
  ["packaging", /\b(?:gift[\s-]*wrap\w*|wrap(?:ping|ped)?|box(?:es)?|packag\w*)\b/i],
  // Repair, servicing, resetting a stone.
  ["repair", /\b(?:repair\w*|servic(?:ed|ing)\b|reset(?:s|ting)?\b)/i],
  // Appraisal.
  ["appraisal", /\bapprais\w*/i],
  // Financing and payment plans.
  ["financing", /\b(?:financ\w*|payment\s+plans?|installments?|pay\s+(?:[\w-]+\s+)?over\s+time)/i],
];

const namesService = (text: string): boolean => SERVICE_FAMILIES.some(([, re]) => re.test(text));

/** Gratis once the not-gratis shapes are gone. */
function readsGratis(text: string): boolean {
  let t = text;
  for (const p of KB_NOT_GRATIS) t = t.replace(p, " ");
  return KB_GRATIS.some((p) => p.test(t));
}

const blank = (m: string): string => " ".repeat(m.length);

/**
 * One promo hit not refused in its clause. "$50 off" is found through
 * figures.ts (F-16), as `moneyOff` does. In a policy-term sentence the
 * item-class sale words are masked first (same length, so clause positions
 * hold); every pattern still runs. A gratis sentence is a promo when
 * time-boxed or when it names no service (item 3); `about` is the chunk's title
 * and summary, the fallback for the service. "spend $X" yields to a free
 * service (decision 181).
 */
function readsPromo(text: string, about = ""): boolean {
  const t = KB_POLICY_TERMS.test(plain(text)) ? plain(text).replace(KB_ITEM_CLASS_SALE, blank) : plain(text);
  const spendOnService = readsGratis(t) && namesService(t.replace(KB_SERVICE_GOODS, blank));
  for (const [i, p] of KB_PROMO_PATTERNS.entries()) {
    if (i === KB_SPEND && spendOnService) continue;
    for (const m of t.matchAll(p)) if (!refused(t, m.index ?? 0, (m.index ?? 0) + m[0].length)) return true;
  }
  let from = 0;
  for (const token of extractMoney(t)) {
    const at = t.indexOf(token, from);
    if (at < 0) continue;
    from = at + token.length;
    if (new RegExp(`^${GAP}(?:${OFF}|discount\\w*|savings?\\b)`, "i").test(t.slice(from)) && !negatedBefore(t, at)) return true;
  }
  if (!readsGratis(t)) return false;
  return KB_TIME_BOX.test(t) || !(namesService(t) || namesService(plain(about)));
}

export type KbSentenceKind = "promo" | "policy" | "figure" | "other";
export interface KbSentence {
  readonly text: string;
  readonly kind: KbSentenceKind;
}

/** Sentence ends, a semicolon, a line break. "$4.00" is one sentence: a split needs a space after the stop. */
const KB_SENTENCE_BREAK = /(?<=[.!?])\s+|;\s*|\n+/;
/** Inside a promo sentence, a joined clause: "bands start at $900, and couples get 15% off". */
const KB_CLAUSE_JOIN = /,\s+(?:and|but|plus)\s+/gi;

function kindOf(text: string, about: string): KbSentenceKind {
  if (readsPromo(text, about)) return "promo";
  if (readsGratis(plain(text))) return "policy";
  return /\d/.test(text) ? "figure" : "other";
}

/**
 * TA-604. A KB fact read as sentences, each a promo, a service policy, a
 * figure or other. A promo sentence whose FIRST clause is clean keeps that
 * clause ("wedding bands start at $900, and couples get 15% off" keeps the
 * price); a promo sentence that opens on the promo is promo whole, because a
 * later clause may lean on it ("Holiday sale: it ends Sunday").
 */
export function readKbSentences(fact: string, about = ""): KbSentence[] {
  const out: KbSentence[] = [];
  for (const raw of fact.split(KB_SENTENCE_BREAK)) {
    const text = raw.trim();
    if (text === "") continue;
    const kind = kindOf(text, about);
    if (kind !== "promo") {
      out.push({ text, kind });
      continue;
    }
    // The first joined clause only: a clean head keeps it, a promo head makes the sentence promo whole.
    const join = [...text.matchAll(KB_CLAUSE_JOIN)][0];
    const cut = join?.index ?? -1;
    if (cut > 0 && !readsPromo(text.slice(0, cut), about)) {
      const head = text.slice(0, cut);
      out.push({ text: head, kind: kindOf(head, about) }, { text: text.slice(cut), kind: "promo" });
    } else {
      out.push({ text, kind });
    }
  }
  return out;
}

/** TA-604. Does any sentence of this KB fact state a promo (tier 2)? */
export function statesPromo(fact: string, about = ""): boolean {
  return readKbSentences(fact, about).some((s) => s.kind === "promo");
}

/**
 * The ways a customer asks about offers, one per line. A question that matches
 * gets the KB's promos (decision 163: an offer-ask is answered from them);
 * every other question gets them removed. A money-off ask is read first by
 * `isConcessionRequest` (TA-384), which this does not replace.
 * AWAITING RATIFICATION (TA-604).
 */
export const OFFER_ASK_PATTERNS: readonly RegExp[] = [
  // "any sales coming up", "running any specials", "any black friday deals", "do you have a discount".
  /\b(?:any|have|has|running|doing|got|offering|run)\s+(?:[\w-]+\s+){0,2}?(?:sales|promos?|promotions?|deals?|specials|discounts?|offers|coupons?|markdowns?|incentives)\b/i,
  // "is anything on sale".
  /\bon\s+sale\b/i,
  // "sales this week", "deals going on", "promotions right now".
  /\b(?:sales?|promos?|promotions?|deals?|specials|discounts?|offers)\s+(?:coming\s+up|this\s+(?:week(?:end)?|month)|going\s+on|right\s+now|today|running|available)\b/i,
  // "promo codes", "a discount code", "coupon code".
  /\b(?:promo|discount|coupon|voucher)\s*codes?\b/i,
  // "a loyalty program", "reward points", "refer a friend".
  /\b(?:loyalty|rewards?|referral)\s+(?:program(?:me)?s?|points?|club|scheme|card)\b|\brefer\s+a\s+friend\b/i,
  // "a student discount", "military pricing", "friends and family rate".
  /\b(?:student|military|veterans?|teachers?|seniors?|employee|first[\s-]*time|friends?\s+(?:and|&)\s+family|vip|members?|insider|preferred)\s+(?:discounts?|pricing|prices?|rates?|offers?|deals?)\b/i,
  // "black friday", "cyber monday", "boxing day".
  /\bblack\s+friday\b|\bcyber\s+monday\b|\bboxing\s+day\b/i,
  // "do you take trade-ins".
  /\btrade[\s-]*ins?\b/i,
  // "a gift with purchase", "free gifts".
  /\bgifts?\s+with\s+(?:a\s+)?purchase\b|\bfree\s+gifts?\b/i,
  // "do you price match".
  /\bprice[\s-]*match\w*/i,
  // "bogo", "buy one get one".
  /\bbogo\b|\bbuy\s+one,?\s+get\s+one\b/i,
  // "anything on clearance".
  /\bclearance\b/i,
];

/** TA-604. Does this customer line ask about offers, sales or promos? */
export function asksAboutOffers(text: string): boolean {
  const line = plain(text);
  return OFFER_ASK_PATTERNS.some((p) => p.test(line));
}

/**
 * TA-1377: the text an unsized discount read by `readUnsizedShopDiscount`
 * carries. A label, never the draft's words, so no message text is copied into
 * a concession (section 10).
 */
const UNSIZED_DISCOUNT_TEXT = "money off with no stated figure";

/**
 * TA-1377 (finding 6): the readers of `isConcessionRequest` that need no
 * figure, the shop-side ones, patterns unchanged. The figure-bearing readers
 * (`FIGURE_OFF`, `BARE_OFF`, `FIGURE_LESS`, `moneyOff`, a fraction) are left
 * to `readDiscountClaims`, which sizes a figure, and to the DISC round 4
 * corpus, which rules some benign ("the deposit is $50 off the final price").
 * A small amount taken off counts only when the line talks money, the rule
 * `MATCH_BARE` already follows: "I'll knock a bit off" with no price word is
 * a KNOWN_MISSES row that discountCorpus.test.ts (agents) pins unheld by the
 * cap at f1743de4, and the comment above `TAKE_OFF` says why it is unsure.
 */
function offersMoneyOffWithoutFigure(line: string): boolean {
  return (
    [DISCOUNT_WORD, PRICE_MATCH, PROMO, MATCH_PRICE, BEAT_BARE, HONOUR_ASK, SOCIAL_PROOF].some((p) => p.test(line)) ||
    ((TAKE_OFF.test(line) || MATCH_BARE.test(line)) && PRICE_CONTEXT.test(line))
  );
}

/**
 * TA-1377 review: is the wording at `text[at, end)` refused in its clause, by
 * the reader `statesPromo` already uses for a KB sentence ("we never have a
 * sale", "there is no sale on bands", "discounts are not offered")? The
 * verifier asks it of a cited fact, so a fact that refuses an offer never
 * grounds the same word offered in a draft. Reads only; `KB_NEGATOR` and
 * `KB_NEGATED_AFTER` are used unchanged.
 */
export function refusedInClause(text: string, at: number, end: number): boolean {
  return refused(text, at, end);
}

/** TA-1377: a percent stated in a clause. */
const STATED_PERCENT = /%|\bper\s?cent\b/i;

/**
 * TA-1377 review: where a draft breaks into clauses, so a percent excuses only
 * the clause it sits in. A sentence end, a semicolon or colon, a line break, a
 * comma, or a joining word between two clauses. Cut fine on purpose: a finer
 * cut excuses less, which is the hold direction.
 */
const CLAUSE_BREAK = /(?<=[.!?;:])\s+|[;\n]+|,\s+|\s+(?:and|but|so|plus|or|then)\s+/i;

/**
 * TA-1377 (finding 6; CLAUDE.md section 0.5, item 25a; decision 3 of the
 * 10-02b list: money off with no stated percent is over any cap). A shop's
 * draft that offers money off with no figure ("I'll give you a discount",
 * "knock a little off the price", "price match any dealer", "beat any quote")
 * gives away an amount nobody stated. `extractConcessions` reads none of it,
 * so the 0% cap never saw it. This reads one unsized discount for such a
 * draft, when `readDiscountClaims` sizes nothing in it, and null otherwise.
 *
 * Money off stated beside a percent in its own clause is left to the sized
 * readers: one they miss ("discounted 20%") is a KNOWN_MISSES row that
 * discountCorpus.test.ts (agents) pins unheld by the cap at f1743de4. The
 * verifier's offer-wording check still reads it. A percent anywhere else in
 * the draft excuses nothing ("14k is 58.3% gold. I can knock a little off the
 * price"), and an offer read only across clauses is never excused.
 *
 * For the verifier only: `extractConcessions` keeps its f1743de4 reading,
 * which the conversation's running tab (replyAgent.ts) and the fixed-line
 * check rely on, and which fixedLineCheck.test.ts pins ("the shared readers
 * alone size nothing here"). The verifier grounds it only on a cited fact
 * the draft quotes word for word (`verifier.ts`).
 */
export function readUnsizedShopDiscount(reply: string): Concession | null {
  const line = plain(reply);
  if (!offersMoneyOffWithoutFigure(line)) return null;
  if (!isConcessionRequest(reply, { author: "shop" })) return null;
  // TA-1378: a sized claim of 0% sizes nothing, so it never excuses money off
  // with no figure ("0 per-cent off. I'll give you a discount."), which the
  // per-cent reading would otherwise let through. Hold direction only.
  if (readDiscountClaims(reply, { author: "shop" }).some((c) => c.percent !== 0)) return null;
  const carrying = line.split(CLAUSE_BREAK).filter((clause) => offersMoneyOffWithoutFigure(clause));
  if (carrying.length > 0 && carrying.every((clause) => STATED_PERCENT.test(clause))) return null;
  return { kind: "discount", percent: null, text: UNSIZED_DISCOUNT_TEXT };
}
